/* ┌──────────────────────────────────────────────────────────────────────────┐
   │  EMPREINTE DU CORPS : 3c885f0a2430b2ef                                  │
   │                                                                          │
   │  ⚠️ CE FICHIER EXISTE EN DEUX EXEMPLAIRES, par necessite : Fiscal et     │
   │  Cadastral sont deux peripheres isoles, sans paquet commun. Ce depot     │
   │  porte deja un SDK cryptographique forke en deux copies qui ont diverge  │
   │  DANS LES DEUX SENS — un correctif applique a une seule n'est pas un     │
   │  correctif, c'est une divergence qui attend son tour.                    │
   │                                                                          │
   │  Emplacements :                                                          │
   │    fiscal-gouvernement/apps/fiscal-admin/src/lib/courrier-cle-agent.js   │
   │    cadastral/src/partage/courrier-cle-agent.js        (VPS)              │
   │                                                                          │
   │  Comparer, sans lire :                                                   │
   │    test -f <f> && tail -n +27 <f> | shasum -a 256 | cut -c1-16 || echo ABSENT │
   │  Les deux doivent rendre la valeur ci-dessus. Si elles different, l'une  │
   │  des deux a ete corrigee seule.                                          │
   │                                                                          │
   │  ⚠️ `e3b0c44298fc1c14` VEUT DIRE « FICHIER ABSENT », PAS « conforme ».   │
   │  C'est le sha256 du VIDE : `tail` sur un fichier inexistant rend zero    │
   │  octet sans erreur, et la commande imprime une empreinte d'allure        │
   │  parfaitement normale. Sans le `test -f`, DEUX copies manquantes         │
   │  s'accordent — la garde rend vert pour la pire des raisons. Mesure du    │
   │  12/09/2026 : la copie cadastral avait ete effacee par un rsync et la    │
   │  comparaison ne l'a pas dit.                                             │
   └──────────────────────────────────────────────────────────────────────────┘ */
/**
 * ════════════════════════════════════════════════════════════════════════════
 *  LA CLE DE L'AGENT — navigateur. Partagée par Mowibo Fiscal et Cadastral.
 * ════════════════════════════════════════════════════════════════════════════
 *
 *  C'est le dernier maillon de l'intégration native, et le plus court : sans
 *  lui, l'agent n'a pas de clé, donc pas de session, donc rien à signer — et
 *  toute la chaîne de sceau en amont ne décrit qu'une intention.
 *
 *  ⚠️ LA CLE PRIVEE EST NON EXTRACTIBLE, ET C'EST LE POINT.
 *  `extractable: false` signifie que même ce script ne peut pas la relire : le
 *  navigateur ne rend JAMAIS sa matière, il accepte seulement de signer avec.
 *  Un script injecté sur la page, une extension, un vidage de IndexedDB volé —
 *  aucun ne récupère de quoi signer ailleurs. C'est ce qui rend défendable
 *  l'affirmation « seul l'agent a pu déposer cet acte ».
 *
 *  ⚠️ CONSEQUENCE A ASSUMER, PAS A CACHER : la clé est liée A CET APPAREIL ET
 *  CE NAVIGATEUR. Un agent qui change de poste, vide ses données de site ou
 *  passe en navigation privée n'a plus de clé — et il ne peut pas la
 *  « récupérer », par construction. La réponse n'est donc pas une sauvegarde
 *  (qui détruirait la garantie ci-dessus) mais la ROTATION signée par
 *  l'ancienne clé, et, si celle-ci est perdue, une ré-inscription
 *  administrative. Le dire ici évite qu'on « règle » un jour le problème en
 *  rendant la clé extractible.
 *
 *  ⚠️ AUCUN REPLI SILENCIEUX SI Ed25519 MANQUE. Certains navigateurs anciens
 *  n'exposent pas Ed25519 dans WebCrypto. Se rabattre sur une implémentation
 *  JavaScript importée d'un CDN mettrait la signature d'actes d'État à la merci
 *  d'un tiers ; se rabattre sur un autre algorithme produirait des signatures
 *  que le rail refuse, sans dire pourquoi. On REFUSE, et on nomme la cause.
 */

const BASE = 'mowibo-courrier';
const MAGASIN = 'cles';
const CLE = 'agent';

/* ───────────────────────────── IndexedDB ──────────────────────────────── */

function ouvrirBase() {
  return new Promise((resoudre, rejeter) => {
    const d = indexedDB.open(BASE, 1);
    d.onupgradeneeded = () => {
      if (!d.result.objectStoreNames.contains(MAGASIN)) d.result.createObjectStore(MAGASIN);
    };
    d.onsuccess = () => resoudre(d.result);
    d.onerror = () => rejeter(d.error || new Error('IndexedDB indisponible'));
  });
}

async function lire(cle) {
  const base = await ouvrirBase();
  return new Promise((resoudre, rejeter) => {
    const r = base.transaction(MAGASIN, 'readonly').objectStore(MAGASIN).get(cle);
    r.onsuccess = () => resoudre(r.result || null);
    r.onerror = () => rejeter(r.error);
  });
}

async function ecrire(cle, valeur) {
  const base = await ouvrirBase();
  return new Promise((resoudre, rejeter) => {
    const t = base.transaction(MAGASIN, 'readwrite');
    t.objectStore(MAGASIN).put(valeur, cle);
    t.oncomplete = () => resoudre();
    t.onerror = () => rejeter(t.error);
  });
}

/* ──────────────────────────── did:key Ed25519 ─────────────────────────── */

const B58 = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

function base58Encode(octets) {
  let n = 0n;
  for (const b of octets) n = (n << 8n) | BigInt(b);
  let out = '';
  while (n > 0n) { out = B58[Number(n % 58n)] + out; n /= 58n; }
  for (const b of octets) { if (b === 0) out = '1' + out; else break; }
  return out;
}

/** `did:key:z…` depuis une clé publique brute de 32 octets (multicodec ed:01). */
function didDepuisClePublique(brute) {
  const avecCodec = new Uint8Array(2 + brute.length);
  avecCodec[0] = 0xed; avecCodec[1] = 0x01;
  avecCodec.set(brute, 2);
  return 'did:key:z' + base58Encode(avecCodec);
}

/* ────────────────────────────── La clé ────────────────────────────────── */

function exigerEd25519() {
  if (!globalThis.crypto?.subtle) {
    throw new Error(
      'WebCrypto indisponible. Une page servie en HTTP simple n\'y a pas droit : '
      + 'ouvrez le service en HTTPS.',
    );
  }
}

/**
 * Rend la clé de l'agent, en l'engendrant au premier appel.
 * @returns {Promise<{ did: string, cle: CryptoKeyPair, neuve: boolean }>}
 */
export async function cleDeLAgent() {
  exigerEd25519();
  const rangee = await lire(CLE);
  if (rangee?.paire?.privateKey) {
    return { did: rangee.did, cle: rangee.paire, neuve: false };
  }

  let paire;
  try {
    paire = await crypto.subtle.generateKey({ name: 'Ed25519' }, false, ['sign', 'verify']);
  } catch (err) {
    // ⚠️ On NOMME la cause plutôt que de se rabattre. Un repli produirait des
    // signatures que le rail refuse, et l'agent chercherait le défaut chez lui.
    throw new Error(
      'Ce navigateur ne sait pas signer en Ed25519 — la signature d\'actes d\'État '
      + 'l\'exige et aucune autre voie n\'est acceptée. Utilisez une version récente '
      + `de Chrome, Edge, Firefox ou Safari. (${err?.message || 'cause inconnue'})`,
    );
  }

  const brute = new Uint8Array(await crypto.subtle.exportKey('raw', paire.publicKey));
  const did = didDepuisClePublique(brute);
  // ⚠️ `extractable: false` a l'engendrement : ce qui est rangé ici est une
  // POIGNEE vers la clé, pas la clé. IndexedDB n'en détient pas la matière.
  await ecrire(CLE, { did, paire, cree_le: new Date().toISOString() });
  return { did, cle: paire, neuve: true };
}

/** Le DID de l'agent, sans engendrer de clé s'il n'en a pas. */
export async function didConnu() {
  const rangee = await lire(CLE);
  return rangee?.did || null;
}

function base64(octets) {
  let s = '';
  for (const o of octets) s += String.fromCharCode(o);
  return btoa(s);
}

/**
 * Signe le message d'un défi et rend la signature en base64.
 *
 * ⚠️ ON SIGNE LA CHAINE RENDUE PAR LE RAIL (`a_signer`), TELLE QUELLE.
 * La reconstruire ici à partir du nonce et de l'identifiant ferait exister
 * DEUX définitions de ce qui est signé, et la seconde dériverait — c'est le
 * défaut qui a déjà produit un SDK forké en deux exemplaires dans ce dépôt.
 */
export async function signerDefi(aSigner) {
  const { cle } = await cleDeLAgent();
  const signature = await crypto.subtle.sign(
    { name: 'Ed25519' }, cle.privateKey, new TextEncoder().encode(aSigner),
  );
  return base64(new Uint8Array(signature));
}

/**
 * Le parcours complet, du premier jour à la session.
 *
 * @param {object} p
 * @param {string} p.externalId    identifiant de l'agent dans son application métier
 * @param {string} p.baseModule    préfixe des routes du module Courrier
 *                                 (ex. `/courrier/agents` côté Cadastral,
 *                                 `/api/v1/courrier/agents` côté Fiscal)
 * @param {function} [p.appeler]   émetteur HTTP de l'application hôte — injecté
 *                                 pour que chaque périmètre garde SES en-têtes
 *                                 d'authentification, ses cookies et son CSRF.
 */
export async function ouvrirSessionCourrier({ externalId, baseModule, appeler }) {
  const poste = appeler || (async (chemin, corps) => {
    const r = await fetch(chemin, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(corps),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      const e = new Error(data.message || data.detail || data.erreur || `HTTP ${r.status}`);
      e.code = data.erreur || data.error || null;
      e.statut = r.status;
      throw e;
    }
    return data;
  });

  const { did, neuve } = await cleDeLAgent();
  if (neuve) {
    // Première fois sur cet appareil : le rail enregistre la clé PUBLIQUE.
    // Écriture unique côté rail — un second enregistrement est refusé en 409.
    await poste(`${baseModule}/cle`, { externalId, signerDid: did, signer_did: did });
  }

  const defi = await poste(`${baseModule}/defi`, { externalId, external_id: externalId });
  const aSigner = defi.a_signer || defi.aSigner;
  if (!aSigner) {
    throw new Error('Le rail n\'a pas rendu de message à signer : session impossible.');
  }
  // ⚠️ Le rail rend aussi le DID qu'il a enregistré. S'il diffère du nôtre, la
  // signature échouera côté serveur avec un motif générique. On le dit ICI,
  // où la cause est lisible : cet appareil n'est pas celui qui a été inscrit.
  const didAttendu = defi.signer_did || defi.signerDid;
  if (didAttendu && didAttendu !== did) {
    throw new Error(
      'La clé enregistrée pour vous appartient à un AUTRE appareil. Cet appareil-ci '
      + 'ne peut pas signer en votre nom. Demandez une rotation de clé à votre '
      + 'administration — elle se signe avec l\'ancienne.',
    );
  }

  const signature = await signerDefi(aSigner);
  return poste(`${baseModule}/session`, {
    externalId, external_id: externalId,
    nonce: defi.nonce,
    signatureB64: signature, signature_b64: signature,
  });
}
